import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'graphite' | 'nocturne' | 'parchemin' | 'mercure';

export const THEMES: readonly Theme[] = ['graphite', 'nocturne', 'parchemin', 'mercure'] as const;

/**
 * Absolute swatches used by the theme-picker preview. These must never
 * follow the currently-active theme — they are the fixed identity of
 * each theme (surface / text / accent).
 */
export const THEME_LABELS: Record<Theme, {
  name: string;
  mode: 'dark' | 'light';
  hint: string;
  swatches: { surface: string; text: string; accent: string };
}> = {
  graphite:  {
    name: 'Graphite',  mode: 'dark',  hint: 'La journée de travail',
    swatches: { surface: 'oklch(0.175 0.008 60)',  text: 'oklch(0.965 0.005 80)',  accent: 'oklch(0.700 0.130 235)' },
  },
  nocturne:  {
    name: 'Nocturne',  mode: 'dark',  hint: 'La salle serveur à 23 h',
    swatches: { surface: 'oklch(0.165 0.032 265)', text: 'oklch(0.960 0.010 250)', accent: 'oklch(0.790 0.160 165)' },
  },
  parchemin: {
    name: 'Parchemin', mode: 'light', hint: 'Le cahier de laboratoire',
    swatches: { surface: 'oklch(0.975 0.006 85)',  text: 'oklch(0.215 0.015 260)', accent: 'oklch(0.400 0.140 255)' },
  },
  mercure:   {
    name: 'Mercure',   mode: 'light', hint: 'La salle de revue lumineuse',
    swatches: { surface: 'oklch(0.980 0.003 250)', text: 'oklch(0.200 0.020 270)', accent: 'oklch(0.520 0.140 285)' },
  },
};

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

/**
 * Applies the theme to <html> via data-theme attribute. The attribute
 * switch triggers instant repaint of all CSS custom properties.
 */
function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'graphite',
      setTheme: (t) => {
        applyTheme(t);
        set({ theme: t });
      },
    }),
    {
      name: 'lab.theme',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);
